import {
  ApiWeeklySchedule,
  ApiWeeklyScheduleEvent,
  InboundMerchantUpdateRequest,
} from 'apis/merchant-onboarding-service/types';
import { WeeklySchedule, WeeklyScheduleEvent } from 'common/components/weekly-schedules/types';
import fromTimePairsByWeekdayToWeeklySchedule from 'modules/self-serve-sign-up/common/WeeklyScheduleInput/fromTimePairsByWeekdayToWeeklySchedule';

import { BusinessDetails, OpeningHours } from './validation';
import fromWeeklyScheduleToTimePairsByWeekday, {
  TimePair,
  TimePairsByWeekday,
} from '../../../../common/components/weekly-schedules/fromWeeklyScheduleToTimePairsByWeekday';
import { InboundMerchant } from '../types';

export const businessDetailsToApi = (formValues: BusinessDetails): InboundMerchantUpdateRequest => {
  const {
    name: formName,
    address: formAddress,
    postCode: formPostCode,
    city: formCity,
    businessName: formBusinessName,
    businessId: formBusinessId,
    businessAddress: formBusinessAddress,
    businessPostCode: formBusinessPostCode,
    businessCity: formBusinessCity,
    businessOtherCity: formBusinessOtherCity,
    isSameAddress,
  } = formValues;
  const name = formName.trim();
  const address = formAddress.trim();
  const postCode = formPostCode.trim();
  const city = formCity.trim();
  const businessName = formBusinessName.trim();
  const businessId = formBusinessId.trim();

  let businessAddress = formBusinessAddress.trim();
  let businessPostCode = formBusinessPostCode.trim();
  let businessCity = formBusinessCity.trim();
  const businessOtherCity = formBusinessOtherCity ? formBusinessOtherCity.trim() : '';

  if (isSameAddress) {
    businessAddress = address;
    businessPostCode = postCode;
    businessCity = city;
  }

  return {
    name: businessName,
    businessType: 'restaurant',
    venues: [
      {
        name,
        address,
        postCode,
        city,
        productLine: 'restaurant',
      },
    ],
    businessId,
    address: businessAddress,
    postCode: businessPostCode,
    city: businessCity === 'other' ? businessOtherCity : businessCity,
  };
};

const transformWeeklyScheduleToApiFormat = (weeklySchedule: WeeklySchedule): ApiWeeklySchedule => {
  const unwrapDateValue = (event: WeeklyScheduleEvent) => ({
    type: event.type,
    value: event.value.$date,
  });

  return {
    monday: weeklySchedule.monday.map(unwrapDateValue),
    tuesday: weeklySchedule.tuesday.map(unwrapDateValue),
    wednesday: weeklySchedule.wednesday.map(unwrapDateValue),
    thursday: weeklySchedule.thursday.map(unwrapDateValue),
    friday: weeklySchedule.friday.map(unwrapDateValue),
    saturday: weeklySchedule.saturday.map(unwrapDateValue),
    sunday: weeklySchedule.sunday.map(unwrapDateValue),
  };
};

export const openingHoursToApi = (formValues: OpeningHours): ApiWeeklySchedule => {
  return transformWeeklyScheduleToApiFormat(fromTimePairsByWeekdayToWeeklySchedule(formValues.openingHours));
};

export const apiWeeklyScheduleToWeeklySchedule = (apiWeeklySchedule: ApiWeeklySchedule): WeeklySchedule => {
  const transformEvent = (event: ApiWeeklyScheduleEvent) => ({
    type: event.type,
    value: {
      $date: event.value,
    },
  });
  return {
    monday: apiWeeklySchedule.monday.map(transformEvent),
    tuesday: apiWeeklySchedule.tuesday.map(transformEvent),
    wednesday: apiWeeklySchedule.wednesday.map(transformEvent),
    thursday: apiWeeklySchedule.thursday.map(transformEvent),
    friday: apiWeeklySchedule.friday.map(transformEvent),
    saturday: apiWeeklySchedule.saturday.map(transformEvent),
    sunday: apiWeeklySchedule.sunday.map(transformEvent),
  };
};

export const timePairsByWeekdayToOpenningHours = (timePairsByWeekday: TimePairsByWeekday): OpeningHours => {
  const transformEvent = (event: TimePair) => ({
    startMinutes: event.start,
    endMinutes: event.end,
  });
  return {
    openingHours: {
      monday: timePairsByWeekday.monday.map(transformEvent),
      tuesday: timePairsByWeekday.tuesday.map(transformEvent),
      wednesday: timePairsByWeekday.wednesday.map(transformEvent),
      thursday: timePairsByWeekday.thursday.map(transformEvent),
      friday: timePairsByWeekday.friday.map(transformEvent),
      saturday: timePairsByWeekday.saturday.map(transformEvent),
      sunday: timePairsByWeekday.sunday.map(transformEvent),
    },
  };
};

export const apiWeeklyScheduleToOpenningHours = (apiWeeklySchedule: ApiWeeklySchedule): OpeningHours =>
  timePairsByWeekdayToOpenningHours(
    fromWeeklyScheduleToTimePairsByWeekday(apiWeeklyScheduleToWeeklySchedule(apiWeeklySchedule)),
  );

export const businessDetailsFromApi = (inboundMerchant: InboundMerchant): BusinessDetails => {
  const inboundMerchantVenue = inboundMerchant.venues.length >= 1 ? inboundMerchant.venues[0] : undefined;

  const isSameAddress =
    inboundMerchantVenue &&
    inboundMerchantVenue.address === inboundMerchant.address &&
    inboundMerchantVenue.city === inboundMerchant.city &&
    inboundMerchantVenue.postCode === inboundMerchant.postCode;

  const formValues: BusinessDetails = {
    name: inboundMerchantVenue?.name || '',
    address: inboundMerchantVenue?.address || '',
    postCode: inboundMerchantVenue?.postCode || '',
    city: inboundMerchantVenue?.city || '',
    isSameAddress,
    businessAddress: isSameAddress ? '' : inboundMerchant.address,
    businessPostCode: isSameAddress ? '' : inboundMerchant.postCode,
    businessCity: isSameAddress ? '' : inboundMerchant.city,
    businessName: inboundMerchant.name,
    businessId: inboundMerchant.businessId,
    businessOtherCity: undefined,
  };

  return formValues;
};
